import { useEffect, useRef, useState, type FormEvent } from "react";
import QRCode from "qrcode";
import {
  EmailAuthProvider,
  getMultiFactorResolver,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendEmailVerification,
  type MultiFactorError,
  type MultiFactorInfo,
  type MultiFactorResolver,
  type RecaptchaVerifier,
  type TotpSecret,
} from "firebase/auth";
import { ShieldCheck } from "lucide-react";
import { Button, Card, CardTitle, Input, Label, cn } from "@clevis/shared-ui";
import { auth } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import {
  authCode,
  e164,
  factorLabel,
  finishAppSetup,
  finishTextSetup,
  newVerifier,
  removeFactor,
  startAppSetup,
  startTextSetup,
  twoStepMessage,
  useTwoStep,
} from "../lib/twoStep";
import { TwoStepChallenge } from "./TwoStepChallenge";

// My profile → Two-step sign-in: turn it on with an authenticator app (Google
// Authenticator, Microsoft Authenticator, 1Password…), add a phone that gets a
// texted code as the backup, or remove one. Firebase asks for your password
// again before changing it if you signed in a while ago.

type Step = { kind: "idle" } | { kind: "app"; secret: TotpSecret; key: string; qr: string } | { kind: "phone" } | { kind: "phoneCode"; verificationId: string; phone: string };

export function TwoStepSetup({ intro, onUnavailable }: { intro?: string; /** Two-step isn't switched on for Clevis (the gate lets people through). */ onUnavailable?: () => void }) {
  const { user } = useAuth();
  const { factors, required, dueAt } = useTwoStep();
  const [step, setStep] = useState<Step>({ kind: "idle" });
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [confirmOff, setConfirmOff] = useState<string | null>(null);
  // Re-checking who you are: what to retry after, and the second step if that needs one.
  const [reauth, setReauth] = useState<null | (() => Promise<void>)>(null);
  const [password, setPassword] = useState("");
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null);
  const verifier = useRef<RecaptchaVerifier | null>(null);
  useEffect(() => () => verifier.current?.clear(), []);

  const usesPassword = !!user?.providerData.some((p) => p.providerId === "password");
  // Firebase sets up two-step only for a confirmed email address (one time).
  const [verified, setVerified] = useState(!!user?.emailVerified);
  const [verifySent, setVerifySent] = useState(false);
  async function sendVerify() {
    setBusy(true);
    setError("");
    try {
      await sendEmailVerification(user!);
      setVerifySent(true);
    } catch (err) {
      setError(twoStepMessage(err));
    } finally {
      setBusy(false);
    }
  }
  async function checkVerified() {
    await user!.reload();
    await user!.getIdToken(true);
    if (auth.currentUser?.emailVerified) setVerified(true);
    else setError("Not confirmed yet. Click the link in the email, then press this again.");
  }
  const hasApp = factors.some((f) => f.factorId === "totp");
  const hasPhone = factors.some((f) => f.factorId === "phone");

  /** Run a change; if Firebase wants a fresh sign-in first, ask for it and try again. */
  async function guarded(fn: () => Promise<void>) {
    setBusy(true);
    setError("");
    setDone("");
    try {
      await fn();
    } catch (err) {
      if (authCode(err) === "auth/requires-recent-login") setReauth(() => fn);
      else {
        if (authCode(err) === "auth/operation-not-allowed") onUnavailable?.();
        setError(twoStepMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }
  async function retryAfterReauth() {
    const again = reauth;
    setReauth(null);
    setResolver(null);
    setPassword("");
    if (again) await guarded(again);
  }
  async function confirmIdentity(e?: FormEvent) {
    e?.preventDefault();
    if (!user) return;
    setBusy(true);
    setError("");
    try {
      if (usesPassword) await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email ?? "", password));
      else await reauthenticateWithPopup(user, new GoogleAuthProvider());
      await retryAfterReauth();
    } catch (err) {
      if (authCode(err) === "auth/multi-factor-auth-required") setResolver(getMultiFactorResolver(auth, err as MultiFactorError));
      else setError(authCode(err) === "auth/invalid-credential" || authCode(err) === "auth/wrong-password" ? "That password isn't right." : twoStepMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const beginApp = () =>
    guarded(async () => {
      const s = await startAppSetup(user!);
      setStep({ kind: "app", secret: s.secret, key: s.key, qr: await QRCode.toDataURL(s.uri, { margin: 1, width: 200 }) });
      setCode("");
    });
  const finishApp = (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== "app") return;
    void guarded(async () => {
      await finishAppSetup(user!, step.secret, code);
      setStep({ kind: "idle" });
      setDone("Two-step sign-in is on. Next time you sign in on a new device, Clevis asks for a code from the app.");
    });
  };
  const sendPhone = (e: FormEvent) => {
    e.preventDefault();
    void guarded(async () => {
      verifier.current ??= newVerifier("two-step-setup-recaptcha");
      const p = e164(phone);
      const id = await startTextSetup(user!, p, verifier.current);
      setStep({ kind: "phoneCode", verificationId: id, phone: p });
      setCode("");
    });
  };
  const finishPhone = (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== "phoneCode") return;
    void guarded(async () => {
      await finishTextSetup(user!, step.verificationId, code);
      setStep({ kind: "idle" });
      setDone(`Backup phone added. If your app isn't handy, Clevis can text ${step.phone} a code instead.`);
    });
  };
  const remove = (f: MultiFactorInfo) =>
    guarded(async () => {
      await removeFactor(user!, f);
      setConfirmOff(null);
      setDone(`Removed ${factorLabel(f).toLowerCase()}.`);
    });

  if (!user) return null;
  const due = new Date(dueAt).toLocaleDateString(undefined, { month: "long", day: "numeric" });
  return (
    <Card className="space-y-3">
      <div className="flex items-start gap-2">
        <ShieldCheck size={18} className={cn("mt-0.5 shrink-0", factors.length ? "text-success" : "text-steel")} />
        <div>
          <CardTitle className="mb-0.5">Two-step sign-in {factors.length ? <span className="text-xs font-normal text-success">on</span> : null}</CardTitle>
          <p className="text-xs text-steel">
            {intro ??
              (factors.length
                ? "Signing in on a new device asks for a code as well as your password. Devices you're already signed in on aren't asked again."
                : required
                  ? `Owners and admins need this${dueAt > Date.now() ? ` by ${due}` : ""}: a code from your phone when you sign in on a new device. It takes two minutes.`
                  : "Add a code from your phone when you sign in on a new device, so a stolen password isn't enough.")}
          </p>
        </div>
      </div>

      {factors.length ? (
        <div className="rounded-lg border border-steel/15">
          {factors.map((f) => (
            <div key={f.uid} className="flex items-center gap-2 border-b border-steel/10 px-3 py-1.5 text-sm last:border-0">
              <span className="min-w-0 flex-1 truncate text-ink">{factorLabel(f)}</span>
              {confirmOff === f.uid ? (
                <span className="flex items-center gap-2 text-xs">
                  {factors.length === 1 && required ? "Owners and admins need one. Remove anyway?" : "Remove it?"}
                  <button className="font-semibold text-danger hover:underline" disabled={busy} onClick={() => void remove(f)}>
                    Remove
                  </button>
                  <button className="text-steel hover:text-ink" onClick={() => setConfirmOff(null)}>
                    Keep
                  </button>
                </span>
              ) : (
                <button className="text-xs text-steel hover:text-danger" onClick={() => setConfirmOff(f.uid)}>
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      ) : null}

      {reauth ? (
        resolver ? (
          <TwoStepChallenge resolver={resolver} onDone={() => void retryAfterReauth()} onCancel={() => { setResolver(null); setReauth(null); }} />
        ) : (
          <form onSubmit={confirmIdentity} className="space-y-2 rounded-lg bg-cream p-3">
            <p className="text-xs text-ink">For your safety, confirm it's you before changing two-step sign-in.</p>
            {usesPassword ? (
              <div>
                <Label>Your Clevis password</Label>
                <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
            ) : null}
            <div className="flex gap-2">
              <Button type="submit" className="px-3 py-1 text-xs" busy={busy}>
                {usesPassword ? "Confirm" : "Confirm with Google"}
              </Button>
              <Button type="button" variant="ghost" className="px-3 py-1 text-xs" onClick={() => setReauth(null)}>
                Cancel
              </Button>
            </div>
          </form>
        )
      ) : step.kind === "app" ? (
        <form onSubmit={finishApp} className="space-y-2 rounded-lg bg-cream p-3">
          <p className="text-xs text-ink">
            1. Open your authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…) and scan this. 2. Type the 6-digit code it shows.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <img src={step.qr} alt="QR code to scan with your authenticator app" className="h-40 w-40 rounded bg-white p-1" />
            <p className="max-w-56 break-all text-[11px] text-steel">
              Can't scan? Enter this key in the app: <span className="font-mono text-ink">{step.key}</span>
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-36">
              <Label>Code from the app</Label>
              <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
            </div>
            <Button type="submit" className="px-3 py-1.5 text-xs" busy={busy} disabled={code.length !== 6}>
              Turn on
            </Button>
            <Button type="button" variant="ghost" className="px-3 py-1.5 text-xs" onClick={() => setStep({ kind: "idle" })}>
              Cancel
            </Button>
          </div>
        </form>
      ) : step.kind === "phone" ? (
        <form onSubmit={sendPhone} className="flex flex-wrap items-end gap-2 rounded-lg bg-cream p-3">
          <div className="min-w-48 flex-1">
            <Label>Cell number</Label>
            <Input type="tel" autoComplete="tel" required value={phone} placeholder="(602) 555-0100" onChange={(e) => setPhone(e.target.value)} />
          </div>
          <Button type="submit" className="px-3 py-1.5 text-xs" busy={busy}>
            Text me a code
          </Button>
          <Button type="button" variant="ghost" className="px-3 py-1.5 text-xs" onClick={() => setStep({ kind: "idle" })}>
            Cancel
          </Button>
        </form>
      ) : step.kind === "phoneCode" ? (
        <form onSubmit={finishPhone} className="flex flex-wrap items-end gap-2 rounded-lg bg-cream p-3">
          <div className="w-36">
            <Label>Code texted to {step.phone}</Label>
            <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
          </div>
          <Button type="submit" className="px-3 py-1.5 text-xs" busy={busy} disabled={code.length !== 6}>
            Add backup phone
          </Button>
          <Button type="button" variant="ghost" className="px-3 py-1.5 text-xs" onClick={() => setStep({ kind: "idle" })}>
            Cancel
          </Button>
        </form>
      ) : !verified && factors.length === 0 ? (
        <div className="space-y-2 rounded-lg bg-cream p-3 text-xs text-ink">
          <p>
            First, confirm your email address ({user.email}). It's a one-time step: we send a link, you click it.
          </p>
          <div className="flex flex-wrap gap-2">
            {!verifySent ? (
              <Button className="px-3 py-1 text-xs" busy={busy} onClick={() => void sendVerify()}>
                Email me the link
              </Button>
            ) : (
              <Button className="px-3 py-1 text-xs" onClick={() => void checkVerified()}>
                I clicked it
              </Button>
            )}
            {verifySent ? (
              <button className="text-steel underline hover:text-ink" onClick={() => void sendVerify()}>
                Send it again
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {!hasApp ? (
            <Button className="px-3 py-1.5 text-xs" busy={busy} onClick={() => void beginApp()}>
              {factors.length ? "Add an authenticator app" : "Set up with an authenticator app"}
            </Button>
          ) : null}
          {!hasPhone ? (
            <Button variant={hasApp ? "primary" : "ghost"} className="px-3 py-1.5 text-xs" onClick={() => { setStep({ kind: "phone" }); setError(""); }}>
              {hasApp ? "Add a backup phone (texted code)" : "Use texted codes instead"}
            </Button>
          ) : null}
        </div>
      )}
      {hasApp && !hasPhone && step.kind === "idle" && !reauth ? <p className="text-[11px] text-steel">A backup phone gets you in if your app's phone is lost or reset.</p> : null}
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      {done ? <p className="text-xs text-success">{done}</p> : null}
      <div id="two-step-setup-recaptcha" />
    </Card>
  );
}
